import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  FaUpload,
  FaSync,
  FaTrash,
  FaEdit,
  FaStore,
  FaBell,
  FaUserCircle,
  FaListUl,
  FaBoxes,
  FaChartLine,
  FaCreditCard,
  FaPlus,
} from 'react-icons/fa';

const PharmacyDashboard = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [pharmacyName, setPharmacyName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [profile, setProfile] = useState({ full_name: '', avatar: '' });
  const [drugList, setDrugList] = useState([]);
  const [manualDrug, setManualDrug] = useState({
    drug_name: '',
    quantity: '',
    price: '',
    expiration_date: '',
    nafdac_number: '',
  });

  const navigate = useNavigate();
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!token) return navigate('/Login');
    const decoded = jwtDecode(token);
    if (decoded.exp * 1000 < Date.now()) return navigate('/Login');
    fetchInventory();
    fetchPharmacyProfile();
    fetchDrugList();
  }, []);

  useEffect(() => {
    const filtered = inventory.filter((item) =>
      item.drug_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInventory(filtered);
  }, [searchTerm, inventory]);

  const fetchInventory = async () => {
    try {
      const res = await axios.get('http://127.0.0.1:8000/pharmacies/get_inventory', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInventory(res.data);
      setFilteredInventory(res.data);
    } catch {
      setError('Failed to load inventory.');
    }
  };

  const fetchPharmacyProfile = async () => {
    try {
      const res = await axios.get('http://127.0.0.1:8000/users/users/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profileData = res.data.profile;
      setPharmacyName(profileData.pharmacy_name || 'Pharmacy');
      setProfile({
        full_name: profileData.full_name || 'Pharmacist',
        avatar: profileData.logo || 'http://127.0.0.1:8000/static/images/profile.png',
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDrugList = async () => {
    try {
      const res = await axios.get('http://127.0.0.1:8000/pharmacies/drugs/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrugList(res.data);
    } catch (err) {
      console.error('Error fetching drugs:', err);
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && selected.type === 'text/csv') {
      setFile(selected);
      setError('');
    } else {
      setError('Only CSV files are allowed.');
    }
  };

  const handleUpload = async () => {
    if (!file) return setError('Please select a CSV file.');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post('http://127.0.0.1:8000/pharmacies/upload-inventory-csv', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Upload successful');
      fetchInventory();
    } catch {
      setError('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!file) return setError('Please select a CSV file.');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.put('http://127.0.0.1:8000/pharmacies/update-inventory-csv', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Update successful');
      fetchInventory();
    } catch {
      setError('Update failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleManualSubmit = async () => {
    // Frontend validation to prevent sending placeholders like mm/dd/yyyy
    if (!manualDrug.drug_name.trim()) {
      setError('Please select a drug name.');
      return;
    }
    if (!manualDrug.quantity) {
      setError('Please enter the quantity.');
      return;
    }
    if (isNaN(manualDrug.quantity) || parseInt(manualDrug.quantity) <= 0) {
      setError('Quantity must be a positive number.');
      return;
    }
    if (!manualDrug.price) {
      setError('Please enter the price.');
      return;
    }
    if (isNaN(manualDrug.price) || parseFloat(manualDrug.price) <= 0) {
      setError('Price must be a valid number.');
      return;
    }
    if (!manualDrug.expiration_date) {
      setError('Please select an expiration date.');
      return;
    }
    if (!manualDrug.nafdac_number.trim()) {
      setError('Please enter the NAFDAC number.');
      return;
    }

    try {
      await axios.post('http://127.0.0.1:8000/pharmacies/add', manualDrug, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Drug added successfully');
      setError('');
      setManualDrug({
        drug_name: '',
        quantity: '',
        price: '',
        expiration_date: '',
        nafdac_number: '',
      });
      fetchInventory();
    } catch (err) {
      const backendError = err?.response?.data?.detail;
      if (Array.isArray(backendError)) {
        setError(backendError.map(e => e.msg).join(', '));
      } else if (typeof backendError === 'string') {
        setError(backendError);
      } else {
        setError('Failed to add drug manually.');
      }
      setSuccess('');
      console.error('Manual submit error:', err);
    }
  };

  const handleDelete = (id) => {
    alert(`Delete medicine with id: ${id}`);
  };

  const handleEdit = (id) => {
    alert(`Edit medicine with id: ${id}`);
  };

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-title">Menu</div>
        <button onClick={() => navigate('/PharmacyProfile')}>
          <FaUserCircle style={{ marginRight: '8px' }} /> Profile
        </button>
        <button onClick={() => navigate('/ManagePharmacyBranch')}>
          <FaListUl style={{ marginRight: '8px' }} /> Manage Branches
        </button>
        <button onClick={() => navigate('/PharmacyBranches')}>
          <FaListUl style={{ marginRight: '8px' }} /> View Branches
        </button>
        <button onClick={() => navigate('/ManageInventory')}>
          <FaBoxes style={{ marginRight: '8px' }} /> Manage Inventory
        </button>
        <button onClick={() => navigate('/analytics')}>
          <FaChartLine style={{ marginRight: '8px' }} /> Analytics
        </button>
        <button onClick={() => navigate('/delete-inventory')}>
          <FaCreditCard style={{ marginRight: '8px' }} /> Subscriptions
        </button>
      </aside>

      <div className="main-dashboard">
        <div className="profile-section">
          <FaBell size={18} color="#ffc107" />
          <Link to="/PharmacyProfile">
            <img
              src={profile.avatar}
              alt="Avatar"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                marginLeft: '10px',
              }}
            />
          </Link>
        </div>

        <div className="dashboard-header">
          <div className="header-top">
            <h2><FaStore /> {pharmacyName || 'Pharmacy Dashboard'}</h2>
          </div>

          <div className="manual-add-section">
            <h4><FaPlus style={{ marginRight: '5px' }} /> Add Inventory Manually</h4>
            <select
              value={manualDrug.drug_name}
              onChange={(e) => setManualDrug({ ...manualDrug, drug_name: e.target.value })}
            >
              <option value="">-- Select Drug --</option>
              {drugList.map((drug, index) => (
                <option key={index} value={drug.name}>{drug.name}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Quantity"
              value={manualDrug.quantity}
              onChange={(e) => setManualDrug({ ...manualDrug, quantity: e.target.value })}
            />
            <input
              type="number"
              placeholder="Price"
              value={manualDrug.price}
              onChange={(e) => setManualDrug({ ...manualDrug, price: e.target.value })}
            />
            <input
              type="date"
              value={manualDrug.expiration_date}
              onChange={(e) => setManualDrug({ ...manualDrug, expiration_date: e.target.value })}
            />
            <input
              type="text"
              placeholder="NAFDAC Number"
              value={manualDrug.nafdac_number}
              onChange={(e) => setManualDrug({ ...manualDrug, nafdac_number: e.target.value })}
            />
            <button onClick={handleManualSubmit}>
              <FaPlus /> Add Drug
            </button>
          </div>

          <input
            type="text"
            placeholder="Search inventory by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <section className="section inventory-section">
          <h3>Inventory Items</h3>
          <ul>
            {filteredInventory.length === 0 && <li>No inventory items found.</li>}
            {filteredInventory.map((item) => (
              <li key={item.id} className="inventory-item">
                <span>
                  <strong>{item.drug_name}</strong><br />
                  Qty: {item.quantity} | Price: ₦{item.price} | Exp: {item.expiration_date} <br />
                  Form: {item.dosage_form} | Strength: {item.strength} | Cat: {item.category}
                </span>
                <div>
                  <button className="edit-btn" onClick={() => handleEdit(item.id)}><FaEdit /></button>
                  <button className="delete-btn" onClick={() => handleDelete(item.id)}><FaTrash /></button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PharmacyDashboard;
